/**
 * Href público de cada tipo legal canónico — único lugar que sabe que
 * terminos/privacidad/cookies tienen ruta dedicada y el resto usa /legal/$type.
 *
 * Client-safe a propósito: solo importa el TIPO de db-service/legal (se borra en
 * compilación), nunca un valor — ese módulo carga el driver de Neon y no puede
 * terminar en el bundle del browser. TypeScript avisa si esta lista se desalinea
 * de LEGAL_DOCUMENT_TYPES (LegalDocumentType exige las 10 keys, ni una menos).
 */
import type { LegalDocumentType } from "@/lib/db-service/legal";

const DEDICATED_HREFS: Partial<Record<LegalDocumentType, string>> = {
  terms_of_service: "/terminos",
  privacy_policy: "/privacidad",
  cookie_policy: "/cookies",
};

export function legalPublicHref(type: string): string {
  return DEDICATED_HREFS[type as LegalDocumentType] ?? `/legal/${type}`;
}

export const LEGAL_DOCUMENT_TITLES: Record<LegalDocumentType, string> = {
  terms_of_service: "Términos y Condiciones",
  privacy_policy: "Política de Privacidad",
  cookie_policy: "Política de Cookies",
  subscription_policy: "Política de Suscripción",
  byo_third_party_policy: "Política de Integraciones BYO",
  kyc_policy: "Política de Verificación Comercial",
  acceptable_use_policy: "Política de Uso Aceptable",
  ip_takedown_policy: "Propiedad Intelectual y Takedown",
  ai_transparency_policy: "Transparencia de IA",
  security_incident_policy: "Seguridad e Incidentes",
};

export const ALL_LEGAL_DOCUMENT_TYPES = Object.keys(
  LEGAL_DOCUMENT_TITLES,
) as LegalDocumentType[];

export const LEGAL_DOCUMENT_DESCRIPTIONS: Record<LegalDocumentType, string> = {
  terms_of_service: "Reglas generales de uso de la plataforma para comercios y su equipo.",
  privacy_policy: "Cómo se recolectan, almacenan y protegen los datos personales.",
  cookie_policy: "Qué cookies y tecnologías similares usa la plataforma, y para qué.",
  subscription_policy: "Condiciones del plan pago: facturación, renovación y cancelación.",
  byo_third_party_policy: "Reglas para conectar tus propias integraciones (BYO) de terceros.",
  kyc_policy: "Cómo funciona la verificación de identidad comercial (KYC) vía Didit.",
  acceptable_use_policy: "Qué usos de la plataforma están permitidos y cuáles no.",
  ip_takedown_policy: "Cómo reportar contenido que infringe propiedad intelectual.",
  ai_transparency_policy: "Qué funciones usan IA y cómo se usan los datos que procesan.",
  security_incident_policy: "Cómo se manejan y comunican los incidentes de seguridad.",
};

export interface LegalDocumentGroup {
  title: string;
  types: LegalDocumentType[];
}

/** Agrupación editorial para la página pública /legal — mismo espíritu que un centro de políticas al estilo Tiendanube. */
export const LEGAL_DOCUMENT_GROUPS: LegalDocumentGroup[] = [
  { title: "Privacidad", types: ["privacy_policy", "cookie_policy"] },
  {
    title: "Términos y Condiciones",
    types: ["terms_of_service", "subscription_policy", "byo_third_party_policy", "acceptable_use_policy"],
  },
  {
    title: "Verificación y Cumplimiento",
    types: ["kyc_policy", "ip_takedown_policy", "ai_transparency_policy", "security_incident_policy"],
  },
];
